import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../context/CurrencyContext';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/common/SEO';

const Cart = () => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language?.startsWith('ar');
    const { cartItems, removeFromCart, updateQuantity, cartCount } = useCart();
    const { formatPrice, currency } = useCurrency();
    const navigate = useNavigate();

    const calculateTotal = () => {
        const priceField = currency === 'SDG' ? 'price_sdg' : (currency === 'EGP' ? 'price_egp' : 'price');
        return cartItems.reduce((acc, item) => acc + (item[priceField] || 0) * item.quantity, 0);
    };

    if (cartItems.length === 0) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center animate-fade-in">
                <div className="w-24 h-24 mb-6 rounded-full bg-gray-50 flex items-center justify-center text-4xl text-gray-200">
                    <i className="fas fa-shopping-bag"></i>
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-4">{t('cart.empty', 'Your cart is empty')}</h2>
                <p className="text-gray-500 mb-8 max-w-md">{t('cart.empty_desc', 'Looks like you haven\'t added anything yet. Explore our collection and find something you love.')}</p>
                <Link to="/shop" className="bg-brand-primary text-white px-10 py-4 rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-brand-primary/20 hover:scale-105 active:scale-95 transition-all">
                    {t('cart.start_shopping', 'Start Shopping')}
                </Link>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <html lang={isArabic ? 'ar' : 'en'} dir={isArabic ? 'rtl' : 'ltr'} />
            </Helmet>
            <SEO />
            <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-20 animate-fade-in">
                <div className="flex flex-col lg:flex-row gap-12">
                    {/* Cart Items List */}
                    <div className="flex-1">
                        <div className="flex items-center justify-between mb-10 pb-6 border-b border-gray-100">
                            <h1 className="text-4xl font-black text-gray-900 tracking-tight">{t('cart.title', 'Shopping Cart')}</h1>
                            <span className="bg-brand-primary/10 text-brand-primary px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest">
                                {cartCount} {t('cart.items', 'Items')}
                            </span>
                        </div>

                        <div className="space-y-6">
                            {cartItems.map((item) => (
                                <div key={item.id || item.product_id} className="group relative flex flex-col sm:flex-row gap-6 p-6 bg-white rounded-3xl border border-gray-50 shadow-sm hover:shadow-xl transition-all duration-500">
                                    {/* Image */}
                                    <div className="w-full sm:w-32 h-40 sm:h-32 bg-gray-50 rounded-2xl overflow-hidden shrink-0">
                                        <img
                                            src={item.imageUrl || '/placeholder-furniture.jpg'}
                                            alt={isArabic && item.name_ar ? item.name_ar : item.name}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                        />
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 flex flex-col">
                                        <div className="flex justify-between items-start mb-2">
                                            <h3 className="text-lg font-black text-gray-900 group-hover:text-brand-primary transition-colors line-clamp-1">
                                                {isArabic && item.name_ar ? item.name_ar : item.name}
                                            </h3>
                                            <button
                                                onClick={() => removeFromCart(item.id || item.product_id)}
                                                className="text-gray-300 hover:text-red-500 transition-colors text-lg"
                                            >
                                                <i className="far fa-trash-alt"></i>
                                            </button>
                                        </div>
                                        <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-4">
                                            {isArabic && item.material_ar ? item.material_ar : item.material}
                                        </p>

                                        <div className="mt-auto flex items-center justify-between">
                                            <div className="flex items-center gap-4 bg-gray-50 p-1.5 rounded-xl border border-gray-100">
                                                <button
                                                    onClick={() => updateQuantity(item.id || item.product_id, item.quantity - 1)}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white shadow-sm text-gray-500 hover:text-brand-primary transition-all active:scale-90"
                                                >
                                                    <i className="fas fa-minus text-[10px]"></i>
                                                </button>
                                                <span className="font-black text-sm w-4 text-center">{item.quantity}</span>
                                                <button
                                                    onClick={() => updateQuantity(item.id || item.product_id, item.quantity + 1)}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white shadow-sm text-gray-500 hover:text-brand-primary transition-all active:scale-90"
                                                >
                                                    <i className="fas fa-plus text-[10px]"></i>
                                                </button>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-xl font-black text-brand-primary">
                                                    {formatPrice({ ...item, price: (item.price || 0) * item.quantity, price_egp: (item.price_egp || 0) * item.quantity, price_sdg: (item.price_sdg || 0) * item.quantity })}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Summary Section */}
                    <div className="w-full lg:w-96">
                        <div className="sticky top-24 bg-white rounded-3xl border border-gray-100 shadow-2xl p-8">
                            <h2 className="text-2xl font-black text-gray-900 mb-8 tracking-tight">{t('cart.summary', 'Order Summary')}</h2>

                            <div className="space-y-4 mb-8">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500 font-bold">{t('cart.subtotal', 'Subtotal')}</span>
                                    <span className="text-gray-900 font-black">{formatPrice({ price: calculateTotal() })}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500 font-bold">{t('cart.shipping', 'Shipping')}</span>
                                    <span className="text-green-500 font-black tracking-widest uppercase text-[10px] bg-green-50 px-3 py-1 rounded-full">{t('cart.free', 'Calculated at checkout')}</span>
                                </div>
                            </div>

                            <div className="pt-6 border-t border-gray-50 mb-10">
                                <div className="flex justify-between items-center">
                                    <span className="text-lg font-black text-gray-900">{t('cart.total', 'Total')}</span>
                                    <div className="text-right">
                                        <span className="text-3xl font-black text-brand-primary block leading-none mb-1">
                                            {formatPrice({ price: calculateTotal() })}
                                        </span>
                                        <span className="text-[10px] text-gray-300 font-black uppercase tracking-widest">{t('cart.inc_tax', 'Incl. local taxes')}</span>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => navigate('/checkout')}
                                className="w-full bg-gray-900 text-white py-5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-gray-200 hover:bg-brand-primary hover:shadow-brand-primary/20 hover:scale-[1.02] active:scale-95 transition-all"
                            >
                                {t('cart.checkout', 'Proceed to Checkout')}
                            </button>

                            <div className="mt-8 flex items-center justify-center gap-4 grayscale opacity-30">
                                <i className="fab fa-cc-visa text-2xl"></i>
                                <i className="fab fa-cc-mastercard text-2xl"></i>
                                <i className="fab fa-cc-paypal text-2xl"></i>
                                <i className="fab fa-cc-stripe text-2xl"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Cart;
